import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Search, X, RotateCcw } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { CommandCard } from "@/components/CommandCard";
import { EmptyState } from "@/components/States";
import { Reveal } from "@/components/Reveal";
import { COMMANDS, COMMAND_CATEGORIES } from "@/data/commands";

const PAGE_SIZE = 60;

export default function Commands() {
  useSEO({
    title: "Commands",
    description:
      "Browse Xevon's full command library — search 360+ commands, filter by module, and view usage, aliases, and descriptions.",
    path: "/commands",
  });

  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return COMMANDS.filter((cmd) => {
      if (category !== "All" && cmd.category !== category) return false;
      if (!q) return true;
      return (
        cmd.name.toLowerCase().includes(q) ||
        cmd.description.toLowerCase().includes(q) ||
        cmd.category.toLowerCase().includes(q) ||
        (cmd.aliases || []).some((a) => a.toLowerCase().includes(q))
      );
    });
  }, [query, category]);

  useEffect(() => setVisible(PAGE_SIZE), [query, category]);

  const hasFilters = query.trim() !== "" || category !== "All";
  const clearFilters = useCallback(() => {
    setQuery("");
    setCategory("All");
  }, []);

  const shown = filtered.slice(0, visible);

  return (
    <>
      <PageHeader
        kicker="Command Library"
        title="Every command. Fully documented."
        description="Search the library or filter by module — pulled straight from Xevon's live command set. Hybrid commands work as both slash and prefix commands."
      />
      <section className="mx-auto w-full max-w-7xl px-6 pb-32" data-testid="commands-section">
        <div className="sticky top-[72px] z-30 -mx-6 border-b border-white/5 bg-black/70 px-6 py-5 backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-500" aria-hidden="true" />
              <input
                ref={inputRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search commands, descriptions, aliases, modules…"
                aria-label="Search commands"
                data-testid="commands-search-input"
                className="w-full rounded-full border border-white/10 bg-white/[0.03] py-3 pl-11 pr-16 text-sm text-paper-100 placeholder:text-paper-500 transition-colors duration-300 focus:border-white/30 focus:bg-white/[0.05] focus:outline-none [&::-webkit-search-cancel-button]:hidden"
              />
              {query ? (
                <button
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  data-testid="commands-search-clear"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/15 p-1 text-paper-400 transition-colors duration-300 hover:border-white/40 hover:text-paper-100"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              ) : (
                <kbd className="pointer-events-none absolute right-4 top-1/2 hidden -translate-y-1/2 rounded-md border border-white/10 px-2 py-0.5 font-mono text-[10px] text-paper-500 sm:block">
                  /
                </kbd>
              )}
            </div>
            {hasFilters && (
              <button
                onClick={clearFilters}
                data-testid="commands-clear-filters"
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-xs font-semibold text-paper-200 transition-colors duration-300 hover:bg-white hover:text-black"
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="hidden sm:inline">Reset</span>
              </button>
            )}
          </div>
          <div
            className="scrollbar-none -mx-1 mt-4 flex gap-2 overflow-x-auto px-1 pb-1"
            role="group"
            aria-label="Filter by category"
            data-testid="category-filters"
          >
            {COMMAND_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                data-testid={`category-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                aria-pressed={category === cat}
                className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                  category === cat
                    ? "border-white bg-white text-black"
                    : "border-white/15 text-paper-400 hover:border-white/40 hover:text-paper-100"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <p className="font-mono text-xs uppercase tracking-wider text-paper-500" data-testid="commands-count">
            {filtered.length} command{filtered.length === 1 ? "" : "s"}
            {category !== "All" && <span className="text-paper-400"> in {category}</span>}
            {query.trim() && <span className="text-paper-400"> matching “{query.trim()}”</span>}
          </p>
          {filtered.length > PAGE_SIZE && (
            <p className="font-mono text-xs text-paper-500" data-testid="commands-shown">
              Showing {Math.min(visible, filtered.length)}
            </p>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-8">
            <EmptyState
              title="No commands found."
              message="Try a different search term, or reset the active filters."
            />
            <div className="mt-6 text-center">
              <button
                onClick={clearFilters}
                data-testid="commands-empty-reset"
                className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-2.5 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                Clear search & filters
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {shown.map((cmd, i) => (
                <Reveal key={cmd.name} delay={(i % 3) * 0.04} y={14}>
                  <CommandCard
                    command={cmd}
                    query={query.trim()}
                    testId={`command-card-${cmd.name.replace(/\s+/g, "-")}`}
                  />
                </Reveal>
              ))}
            </div>
            {visible < filtered.length && (
              <div className="mt-12 text-center">
                <button
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  data-testid="commands-load-more"
                  className="rounded-full border border-white/20 px-10 py-3 text-sm font-semibold text-paper-100 transition-colors duration-300 hover:bg-white hover:text-black"
                >
                  Load more ({filtered.length - visible} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
